/**
 * Recurring Bills & Subscriptions Tracker
 * Tracks recurring bills with frequency, next payment date, and monthly commitment summary
 * Persists to localStorage for demo mode, Supabase for authenticated users
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Plus,
  X,
  Trash2,
  Calendar,
  DollarSign,
  Clock,
  AlertTriangle,
  CreditCard,
  Zap,
  Wifi,
  Shield,
  Car,
  Home,
  Tv,
  Music
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const CATEGORY_ICONS = {
  'Subscription': <Tv className="w-4 h-4" />,
  'Music': <Music className="w-4 h-4" />,
  'Internet': <Wifi className="w-4 h-4" />,
  'Insurance': <Shield className="w-4 h-4" />,
  'Auto': <Car className="w-4 h-4" />,
  'Housing': <Home className="w-4 h-4" />,
  'Utility': <Zap className="w-4 h-4" />,
  'Credit Card': <CreditCard className="w-4 h-4" />,
  'Other': <DollarSign className="w-4 h-4" />
};

const FREQUENCY_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  weekly: 'Weekly'
};

const STORAGE_KEY = 'recurring_bills';

const normalizeBill = (bill) => ({
  id: bill.id,
  name: bill.name || '',
  amount: Number(bill.amount) || 0,
  frequency: bill.frequency || 'monthly',
  category: bill.category || bill.category_id || 'Other',
  nextPaymentDate: bill.nextPaymentDate || bill.next_payment_date || '',
  active: bill.active !== false,
  createdAt: bill.createdAt || bill.created_at
});

const getStorageKey = (userId, isDemo) => `${STORAGE_KEY}_${isDemo ? 'demo' : (userId || 'local')}`;

const createBillId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const loadBills = (storageKey) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey) || '[]').map(normalizeBill);
  } catch { return []; }
};

const saveBills = (storageKey, bills) => {
  localStorage.setItem(storageKey, JSON.stringify(bills));
};

const getNextPayment = (bill) => {
  if (!bill.nextPaymentDate) return null;
  return new Date(bill.nextPaymentDate);
};

const getDaysUntil = (date) => {
  if (!date) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

const BillsTracker = ({ formatCurrency: fmtCur, userId = null, isDemo = false }) => {
  const storageKey = useMemo(() => getStorageKey(userId, isDemo), [userId, isDemo]);
  const [bills, setBills] = useState(() => loadBills(storageKey));
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBill, setNewBill] = useState({
    name: '',
    amount: '',
    frequency: 'monthly',
    category: 'Subscription',
    nextPaymentDate: ''
  });

  const formatMoney = useCallback((value) => (
    fmtCur ? fmtCur(value) : `$${(Number(value) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  ), [fmtCur]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userId || isDemo) {
        setBills(loadBills(storageKey));
        return;
      }

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('recurring_bills')
          .select('*')
          .eq('user_id', userId)
          .order('next_payment_date', { ascending: true });

        if (error) throw error;
        if (!cancelled) setBills((data || []).map(normalizeBill));
      } catch (error) {
        console.warn('Falling back to local recurring bills:', error.message);
        if (!cancelled) setBills(loadBills(storageKey));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId, isDemo, storageKey]);

  const persistBills = useCallback(async (nextBills, operation = null) => {
    setBills(nextBills);
    saveBills(storageKey, nextBills);

    if (!userId || isDemo || !operation) return;

    try {
      if (operation.type === 'insert') {
        const bill = operation.bill;
        const { error } = await supabase.from('recurring_bills').insert({
          id: bill.id,
          user_id: userId,
          name: bill.name,
          amount: bill.amount,
          currency: 'USD',
          frequency: bill.frequency,
          category_id: bill.category,
          next_payment_date: bill.nextPaymentDate || null,
          active: bill.active
        });
        if (error) throw error;
      } else if (operation.type === 'update') {
        const bill = operation.bill;
        const { error } = await supabase
          .from('recurring_bills')
          .update({ active: bill.active })
          .eq('id', bill.id)
          .eq('user_id', userId);
        if (error) throw error;
      } else if (operation.type === 'delete') {
        const { error } = await supabase
          .from('recurring_bills')
          .delete()
          .eq('id', operation.id)
          .eq('user_id', userId);
        if (error) throw error;
      }
    } catch (error) {
      console.warn('Recurring bills Supabase sync failed:', error.message);
    }
  }, [isDemo, storageKey, userId]);

  const monthlyCommitment = useMemo(() => {
    return bills.filter(bill => bill.active !== false).reduce((sum, bill) => {
      const amount = parseFloat(bill.amount) || 0;
      switch (bill.frequency) {
        case 'weekly': return sum + (amount * 52 / 12);
        case 'monthly': return sum + amount;
        case 'quarterly': return sum + (amount / 3);
        case 'yearly': return sum + (amount / 12);
        default: return sum + amount;
      }
    }, 0);
  }, [bills]);

  const yearlyCommitment = useMemo(() => monthlyCommitment * 12, [monthlyCommitment]);

  const upcomingBills = useMemo(() => {
    return bills
      .filter(b => b.active !== false)
      .map(b => ({ ...b, daysUntil: getDaysUntil(getNextPayment(b)) }))
      .sort((a, b) => (a.daysUntil ?? 999) - (b.daysUntil ?? 999));
  }, [bills]);

  const overdueBills = useMemo(() => {
    return upcomingBills.filter(b => b.daysUntil !== null && b.daysUntil < 0);
  }, [upcomingBills]);

  const dueSoonBills = useMemo(() => {
    return upcomingBills.filter(b => b.daysUntil !== null && b.daysUntil >= 0 && b.daysUntil <= 7);
  }, [upcomingBills]);

  const handleAddBill = () => {
    if (!newBill.name || !newBill.amount) return;
    const bill = {
      id: createBillId(),
      ...newBill,
      amount: parseFloat(newBill.amount) || 0,
      active: true,
      createdAt: new Date().toISOString()
    };
    const updated = [...bills, bill];
    persistBills(updated, { type: 'insert', bill });
    setNewBill({ name: '', amount: '', frequency: 'monthly', category: 'Subscription', nextPaymentDate: '' });
    setShowAddForm(false);
  };

  const handleDeleteBill = (id) => {
    const updated = bills.filter(b => b.id !== id);
    persistBills(updated, { type: 'delete', id });
  };

  const handleToggleActive = (id) => {
    const updated = bills.map(b => b.id === id ? { ...b, active: !b.active } : b);
    const bill = updated.find(b => b.id === id);
    persistBills(updated, { type: 'update', bill });
  };

  return (
    <div data-testid="bills-tracker" className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Monthly</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {formatMoney(monthlyCommitment)}
          </p>
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Yearly</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {formatMoney(yearlyCommitment)}
          </p>
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Overdue</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{overdueBills.length}</p>
        </div>
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-gray-500 dark:text-gray-400">Due Soon</span>
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{dueSoonBills.length}</p>
        </div>
      </div>

      {/* Add Bill Button/Form */}
      {!showAddForm ? (
        <button
          data-testid="btn-add-bill"
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Bill / Subscription
        </button>
      ) : (
        <div className="p-4 bg-white dark:bg-gray-900 rounded-xl border border-blue-200 dark:border-blue-900 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Add Recurring Bill</h4>
            <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <input
              data-testid="input-bill-name"
              type="text"
              placeholder="Bill name"
              value={newBill.name}
              onChange={(e) => setNewBill(prev => ({ ...prev, name: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-950 focus:ring-2 focus:ring-blue-500"
            />
            <input
              data-testid="input-bill-amount"
              type="number"
              placeholder="Amount"
              value={newBill.amount}
              onChange={(e) => setNewBill(prev => ({ ...prev, amount: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-950 focus:ring-2 focus:ring-blue-500"
            />
            <select
              data-testid="select-bill-frequency"
              value={newBill.frequency}
              onChange={(e) => setNewBill(prev => ({ ...prev, frequency: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-950 focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(FREQUENCY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              data-testid="select-bill-category"
              value={newBill.category}
              onChange={(e) => setNewBill(prev => ({ ...prev, category: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-950 focus:ring-2 focus:ring-blue-500"
            >
              {Object.keys(CATEGORY_ICONS).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <input
              data-testid="input-bill-next-payment"
              type="date"
              value={newBill.nextPaymentDate}
              onChange={(e) => setNewBill(prev => ({ ...prev, nextPaymentDate: e.target.value }))}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-950 focus:ring-2 focus:ring-blue-500"
            />
            <button
              data-testid="btn-save-bill"
              onClick={handleAddBill}
              className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-xs text-gray-400">Syncing bills…</div>
      )}

      {/* Bills List */}
      {upcomingBills.length === 0 ? (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500">
          <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No recurring bills tracked yet.</p>
          <p className="text-xs mt-1">Add subscriptions, rent, utilities, and other recurring expenses.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {upcomingBills.map(bill => {
            const icon = CATEGORY_ICONS[bill.category] || CATEGORY_ICONS['Other'];
            const daysUntil = bill.daysUntil;
            const urgency = daysUntil !== null
              ? daysUntil < 0 ? 'overdue'
              : daysUntil <= 3 ? 'urgent'
              : daysUntil <= 7 ? 'soon'
              : 'normal'
              : 'normal';

            const urgencyStyles = {
              overdue: 'border-red-300 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20',
              urgent: 'border-amber-300 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20',
              soon: 'border-yellow-200 dark:border-yellow-900 bg-yellow-50/30 dark:bg-yellow-950/10',
              normal: 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
            };

            return (
              <div
                key={bill.id}
                data-testid={`bill-item-${bill.id}`}
                className={`flex items-center justify-between p-3 rounded-xl border ${urgencyStyles[urgency]} ${bill.active === false ? 'opacity-50' : ''} transition-all`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${bill.active === false ? 'bg-gray-100 dark:bg-gray-800' : 'bg-blue-50 dark:bg-blue-950/40'} text-blue-600`}>
                    {icon}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{bill.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{FREQUENCY_LABELS[bill.frequency] || bill.frequency}</span>
                      <span>•</span>
                      <span>{bill.category}</span>
                      {daysUntil !== null && (
                        <>
                          <span>•</span>
                          <span className={urgency === 'overdue' ? 'text-red-500 font-medium' : urgency === 'urgent' ? 'text-amber-600 font-medium' : ''}>
                            {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? 'Due today' : `${daysUntil}d`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatMoney(parseFloat(bill.amount) || 0)}
                  </span>
                  <button
                    onClick={() => handleToggleActive(bill.id)}
                    className={`text-xs px-2 py-1 rounded ${bill.active === false ? 'text-gray-400 hover:text-gray-600' : 'text-gray-400 hover:text-gray-600'}`}
                    title={bill.active === false ? 'Reactivate' : 'Pause'}
                  >
                    {bill.active === false ? 'Paused' : 'Active'}
                  </button>
                  <button
                    onClick={() => handleDeleteBill(bill.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                    aria-label={`Delete ${bill.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BillsTracker;
